/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import { ApiContext } from '../../../../../context/ApiContext';
import { ConversionType } from '../../../../../main';
import {
  euiDefaultUnit,
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD
} from '../../../../shared/Utils/Constants';
import { getProgressBarUnitScaledData } from '../../../../shared/Utils/portfolioDataFormatter';
import {
  getSpanDetails,
  getPerformanceGraphData,
  getPerformanceGraphDataNew
} from '../../../../shared/Utils/Utils';
import { IEmaasEnergyUsageComponentData } from '../../../IEnergyManagementProps';
import {
  convertDataforPortfolioBreakdownByMeter,
  convertDataforPortfolioEnergyBreakdownBySite,
  getNullToZero,
  headersEnergyUsageBasic
} from '../../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import { IMeterDataProps } from '../../../../shared/BreakdownByMeter/BreakdownByMeterProps';
import {
  getEnergyEndDate,
  getToUnitForChart
} from '../../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import { getEuiUnitDisplayName } from '../../../../shared/Utils/unitHandling/EuiUnitHandling';
import CommonEnergyComponent from '../../../../shared/EnergyKPI/CommonEnergyComponent';
import CommonEnergyComponentSeparate from '../../../../shared/EnergyKPI/CommonEnergyComponentSeparate';

interface IEmaasEnergyUsageComponentProps {
  data: IEmaasEnergyUsageComponentData;
  viewType?: string;
  type?: string;
  forGraph?: boolean;
  forSummary?: boolean;
  forTable?: boolean;
  checkAllSitesAndMonthData?: boolean;
  dataSourceType?: string;
}

export const PortfolioEnergyCardSeparate = (
  props: IEmaasEnergyUsageComponentProps
) => {
  const [t] = useTranslation();
  let { data, viewType, checkAllSitesAndMonthData } = props;
  const apiContext = useContext(ApiContext);
  const { redirect, preferredEuiUnit, userPreferedUnit } = apiContext;

  let fromUnit: string;
  // if (true) {
  //   fromUnit = userPreferedUnit as any;
  // } else {
  //   fromUnit = energyMeasurementUnit;
  // }

  fromUnit = userPreferedUnit as any;

  // let {
  //   summary: {
  //     totalConsumption,
  //     totalBaselineConsumption,
  //     totalEnergyDeviation,
  //     totalEnergyDeviationPercentage,
  //     absoluteTotalBaselineConsumption,
  //     absoluteTotalConsumption
  //   },
  //   breakdownBySite,
  //   totalSiteCount,
  //   aggregationsStartDate,
  //   aggregationsEndDate,
  //   performanceAggregationApplied,
  //   performanceAggregation,
  //   energyMeasurementUnit,
  //   euiEnergyMeasuringUnit,
  //   rootNodeId
  // } = data;

  const onViewDetailsClick = () => {
    if (redirect)
      redirect(`/energyViewDetails/root/${data.rootNodeId}/portfolio-view`);
  };
  const dateData = getSpanDetails(
    data.aggregationsStartDate,
    data.aggregationsEndDate
  );

  let consumptionBreakdownMeterData: any;
  if (props.forTable) {
    consumptionBreakdownMeterData = convertDataforPortfolioEnergyBreakdownBySite(
      data.breakdownBySite,
      fromUnit, //energyMeasurementUnit,
      data.euiEnergyMeasuringUnit || euiDefaultUnit,
      preferredEuiUnit || '',
      undefined,
      data.energyMeasurementUnit,
      apiContext.showChangeWhenData
    );
  }
  let values: any;
  if (props.forTable) {
    values = consumptionBreakdownMeterData.values;
  }

  let progressBarUnitScaledData: any;
  if (props.forSummary) {
    progressBarUnitScaledData = getProgressBarUnitScaledData(
      ConversionType.ELECTRICITY,
      {
        previousPeriod: data.summary.totalBaselineConsumption,
        actual: data.summary.totalConsumption
      },
      data.summary.totalEnergyDeviation,
      fromUnit, //energyMeasurementUnit,
      undefined,
      data.summary.absoluteTotalBaselineConsumption,
      data.summary.absoluteTotalConsumption,
      data.energyMeasurementUnit,
      apiContext.showChangeWhenData,
      true
    );
  }
  let ConvertToUnit: any;
  if (props.forGraph) {
    ConvertToUnit = getToUnitForChart(
      data.performanceAggregation,
      ConversionType.ELECTRICITY,
      fromUnit //energyMeasurementUnit
    );
  }

  let comparisonData: any;
  if (props.forGraph) {
    comparisonData =
      data.performanceAggregationApplied === 'Hourly'
        ? getPerformanceGraphData(
            data.performanceAggregation,
            data.energyMeasurementUnit,
            ConvertToUnit,
            data.performanceAggregationApplied,
            ConversionType.ELECTRICITY
            // aggregationsStartDate,
            // aggregationsEndDate
          )
        : getPerformanceGraphDataNew(
            data.performanceAggregation,
            data.energyMeasurementUnit,
            ConvertToUnit,
            data.performanceAggregationApplied,
            ConversionType.ELECTRICITY,
            data.aggregationsStartDate,
            data.aggregationsEndDate
          );
  }

  let percentSavingsForProgressBar: any;
  if (props.forSummary) {
    percentSavingsForProgressBar = parseFloat(
      getNullToZero(data.summary.totalEnergyDeviationPercentage)?.toFixed(
        KPI_PERCENTAGE_DECIMAL_PRECISION
      )
    );
  }

  let aggregationEnergyData: any;
  if (props.forSummary) {
    const savingPercent =
      apiContext.showChangeWhenData &&
      progressBarUnitScaledData.deviation !== null &&
      (isNaN(Number(progressBarUnitScaledData.actual)) ||
        isNaN(Number(progressBarUnitScaledData.previousPeriod)) ||
        Number(progressBarUnitScaledData.actual) === 0 ||
        Number(progressBarUnitScaledData.previousPeriod) === 0 ||
        Math.abs(Number(percentSavingsForProgressBar)) >
          PERCENT_CHANGE_THRESHOLD ||
        !checkAllSitesAndMonthData);

    aggregationEnergyData = {
      actual: progressBarUnitScaledData.actual,
      previousPeriod: progressBarUnitScaledData.previousPeriod,
      absoluteActual: progressBarUnitScaledData.absoluteActual,
      absolutePrevPeriod: progressBarUnitScaledData.absolutePreviousPeriod,
      actualUnit: progressBarUnitScaledData.actualUnit,
      baselineUnit: progressBarUnitScaledData.baselineUnit,
      changeUnit: progressBarUnitScaledData.changeUnit,
      savings: savingPercent ? null : progressBarUnitScaledData.deviation,
      savingsPercentage: savingPercent
        ? null
        : parseFloat(
            getNullToZero(data.summary.totalEnergyDeviationPercentage)?.toFixed(
              KPI_PERCENTAGE_DECIMAL_PRECISION
            )
          )
    };
  }

  const convertedEuiUnit = getEuiUnitDisplayName(preferredEuiUnit);
  const energyComponentProps = {
    onViewDetailsClick: onViewDetailsClick,
    ...(props.forSummary && {
      progressBarData: {
        aggregationData: aggregationEnergyData,
        unit: progressBarUnitScaledData.ConvertedUnit,
        deviationScaledData: progressBarUnitScaledData.deviationScaledData
      }
    }),
    ...(props.forGraph && {
      graphData: {
        chartSeries: comparisonData,
        startTime: data.aggregationsStartDate,
        endTime: data.aggregationsEndDate,
        aggregationType: data.performanceAggregationApplied
      }
    }),
    ...(props.forTable && {
      tableData: {
        columnHeaders: headersEnergyUsageBasic(
          t,
          viewType,
          data.totalSiteCount,
          '',
          undefined,
          convertedEuiUnit
        ),
        meterData: (values as unknown) as IMeterDataProps[]
      }
    }),
    dateData: dateData,
    type: props.type,
    energyMeasurementUnit: data.energyMeasurementUnit,
    fromCarbonKPI: true,
    originalUnit: data.energyMeasurementUnit,
    conversionType: ConversionType.ELECTRICITY,
    dataSourceType: props.dataSourceType
  };

  return (
    <CommonEnergyComponentSeparate
      forSummary={props.forSummary}
      forGraph={props.forGraph}
      forTable={props.forTable}
      {...energyComponentProps}
    />
  );
};
export default memo(PortfolioEnergyCardSeparate);
